import { ICourse } from '@/types/courses';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Modal from '@ui/modal/modal';
import OptionalComponent from '@ui/optional-component/optional-component';
import WaitingIndicator from '@ui/waiting-indicator/waiting-indicator';
import Text from '@ui/text/text/text';
import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import CourseBadge from '../icons/course-badge';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorsCssMixVariable from '@/utils/colors/get-theme-color-css-mix-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';

type AddFromLibraryModalProps = {
  open: boolean;
  onClose: () => void;
  selectedCourse: ICourse | null;
  onSelectCourse: (course: ICourse | null) => void;
  loadingLibrary: boolean;
  libraryCourses: ICourse[];
  onAddFromLibrary: () => void;
  onCreateNew: () => void;
};

export default function AddFromLibraryModal({
  open,
  onClose,
  selectedCourse,
  onSelectCourse,
  loadingLibrary,
  libraryCourses,
  onAddFromLibrary,
  onCreateNew,
}: AddFromLibraryModalProps) {
  const handleClose = () => {
    onClose();
    onSelectCourse(null);
  };

  return (
    <Modal
      open={open}
      customSize={{
        width: 'fit-content',
        height: 'auto',
        maxHeight: '600px',
        minHeight: '200px',
        maxWidth: 'calc(100vw - 80px)',
        minWidth: '690px',
      }}
      contentWrapperClassName="padding-24 gap-24"
      onRequestClose={handleClose}
      RenderHeaderContent={() => {
        return (
          <Container>
            <Text color="var(--gray-100)" className="H4 margin-b-8">
              Add course from library
            </Text>
            <Text color="var(--gray-400)" className="P1">
              Courses from projects that have been saved as templates.
            </Text>
          </Container>
        );
      }}
      RenderFooterContent={() => {
        return (
          <Container className="flex jc-flex-end ai-center gap-16">
            <Button design="LocalBttnSecHalfTransp" designSize="large" onClick={handleClose}>
              <Text className="L1">Cancel</Text>
            </Button>
            <OptionalComponent display={!!libraryCourses.length}>
              <Button
                backgroundColor={getThemeColorCSSVariable()}
                hoverBackgroundColor={getThemeColorsCssMixVariable()}
                designSize="large"
                onClick={onAddFromLibrary}
                disabled={!selectedCourse}
              >
                <Text className="L1" color={getThemeColorContrastCSSVariable()}>
                  Save
                </Text>
              </Button>
            </OptionalComponent>
            <OptionalComponent display={!libraryCourses.length}>
              <Button
                backgroundColor={getThemeColorCSSVariable()}
                hoverBackgroundColor={getThemeColorsCssMixVariable()}
                designSize="large"
                onClick={onCreateNew}
              >
                <Text className="L1" color={getThemeColorContrastCSSVariable()}>
                  Create new
                </Text>
              </Button>
            </OptionalComponent>
          </Container>
        );
      }}
    >
      <>
        <OptionalComponent display={loadingLibrary}>
          <div className="flex ai-center jc-center height-60">
            <WaitingIndicator />
          </div>
        </OptionalComponent>
        <OptionalComponent display={!libraryCourses.length && !loadingLibrary}>
          <Container className="flex ai-center flex-column padding-40 gap-10">
            <Text className="H4">Library empty</Text>
            <Text className="P1">Create a new course from scratch</Text>
          </Container>
        </OptionalComponent>
        <OptionalComponent display={!!libraryCourses.length && !loadingLibrary}>
          <Container
            background="var(--gray-800)"
            className="create-courses-container margin-1"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '8px',
            }}
          >
            {libraryCourses.map((course, idx) => {
              const badgeColors =
                idx % 2 === 0 ? { start: '#5AE4FF', end: '#A7FF7B' } : { start: '#FF6AE6', end: '#55D6F0' };

              const cardColors =
                idx % 2 === 0
                  ? { start: 'rgb(241, 115, 230, 0.1)', end: 'rgb(118, 194, 238, 0.1)' }
                  : { start: 'rgb(95, 219, 229, 0.1)', end: 'rgb(146, 238, 139, 0.1)' };

              return (
                <Button
                  height="117px"
                  width="208px"
                  key={course.uuid}
                  className="flex-column ai-flex-start jc-space-between gap-12 padding-8 radius-12"
                  style={{
                    background: `linear-gradient(to right, ${cardColors.start} , ${cardColors.end})`,
                    border:
                      selectedCourse?.uuid === course.uuid ? 'solid 2px var(--gray-200)' : 'solid 2px transparent',
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    onSelectCourse(selectedCourse && selectedCourse.uuid === course.uuid ? null : course);
                  }}
                >
                  <div>
                    <div className="position-absolute">
                      <CourseBadge height={32} width={32} startColor={badgeColors.start} endColor={badgeColors.end} />
                    </div>
                    <div className="position-absolute top-13 left-19">
                      <Icon name={ICONS.TROPHY_FILLED} size={10} color="var(--gray-1200)" />
                    </div>
                  </div>
                  <div className="flex-column gap-4">
                    <Text className="C1" color="var--gray-200)">
                      {course.requirements?.length || '0'}{' '}
                      {course.requirements?.length === 1 ? 'requirement' : 'requirements'}
                    </Text>
                    <Text className="H6">{course.name.base}</Text>
                  </div>
                </Button>
              );
            })}
          </Container>
        </OptionalComponent>
      </>
    </Modal>
  );
}
